import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react'
import { notification } from 'antd'
import { useSelector } from 'react-redux'
import { axiosInstance } from '../interceptor/axios_interceptor'

export interface PrintConfig {
  // cuisine label → printer name, e.g. {"North Indian":"North","South Indian":"South","default":"Kitchen"}
  cuisineRouting: Record<string, string>
  // Windows printer name that receives full bill/invoice prints
  invoicePrinter?: string
  // Windows printer name that receives a master KOT for all items
  kotPrinter?: string
}

interface PrintConfigContextType {
  printConfig: PrintConfig
  loading: boolean
  save: (
    cuisineRouting: Record<string, string>,
    invoicePrinter?: string,
    kotPrinter?: string
  ) => Promise<void>
  reload: () => void
}

const DEFAULT_CONFIG: PrintConfig = { cuisineRouting: {} }

const PrintConfigContext = createContext<PrintConfigContextType | undefined>(
  undefined
)

export const usePrintConfig = () => {
  const ctx = useContext(PrintConfigContext)
  if (!ctx)
    throw new Error('usePrintConfig must be used within PrintConfigProvider')
  return ctx
}

export const PrintConfigProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const restaurantId = useSelector((state: any) => state.user.restaurantId)
  const [printConfig, setPrintConfig] = useState<PrintConfig>(DEFAULT_CONFIG)
  const [loading, setLoading] = useState(false)

  const fetch = useCallback(async () => {
    if (!restaurantId) return
    setLoading(true)
    try {
      const res = await axiosInstance.get(
        `/api/vendor/printConfig/${restaurantId}`
      )
      let parsedConfig = res.data?.printConfig
      if (typeof parsedConfig === 'string') {
        try {
          parsedConfig = JSON.parse(parsedConfig)
        } catch (e) {
          console.error('Failed to parse printConfig string', e)
        }
      }
      setPrintConfig(parsedConfig ?? DEFAULT_CONFIG)
    } catch {
      // Non-fatal — app works without routing config, everything routes to default
    } finally {
      setLoading(false)
    }
  }, [restaurantId])

  useEffect(() => {
    fetch()
  }, [fetch])

  const save = async (
    cuisineRouting: Record<string, string>,
    invoicePrinter?: string,
    kotPrinter?: string
  ) => {
    try {
      if (restaurantId) {
        await axiosInstance.put(`/api/vendor/printConfig/${restaurantId}`, {
          cuisineRouting,
          invoicePrinter,
          kotPrinter
        })
      }
      setPrintConfig({ cuisineRouting, invoicePrinter, kotPrinter })
      notification.success({
        message: 'Print Config Saved',
        description: 'Printer routing configuration updated successfully.',
        placement: 'bottomRight'
      })
    } catch (error) {
      console.error('Failed to save print config', error)
      notification.error({
        message: 'Save Failed',
        description: 'Could not save printer configuration.',
        placement: 'bottomRight'
      })
    }
  }

  return (
    <PrintConfigContext.Provider
      value={{ printConfig, loading, save, reload: fetch }}
    >
      {children}
    </PrintConfigContext.Provider>
  )
}

/**
 * Given an order item's cuisine and the current routing config, returns the
 * printer role to use. Falls back to config["default"], then to "KITCHEN".
 */
export const resolveRole = (
  cuisine: string | undefined,
  routing: Record<string, string>
): string => {
  if (cuisine) {
    const normalizedCuisine = cuisine.trim().toLowerCase()
    for (const [key, val] of Object.entries(routing)) {
      if (key.trim().toLowerCase() === normalizedCuisine && val) {
        return val
      }
    }
  }
  return routing['default'] ?? 'KITCHEN'
}
